import { Mail, MapPin, Phone } from "lucide-react";
import type { Companies, Contacts } from "@/src/gql/graphql";
import {
  COMPANY_LINE_BADGE_LABEL,
  inheritedMethodEntityContext,
  inheritedMethodTooltip,
  resolveInheritedContactMethods,
  type CompanySharedMethods,
  type InheritedContactMethod,
} from "@/lib/contacts/company-line/inherited-methods";
import type { DetailHeaderModel, HeaderFact, HeaderMetric, HeaderSubtitleSegment } from "../model";
import { formatHeaderDate } from "../model";

/** Scalar columns read straight off the contact row. */
const CONTACT_HEADER_SCALAR_FIELDS = [
  "title",
  "company_id",
  "company_name",
  "email",
  "email_aliases",
  "phone",
  "phone_aliases",
  "is_company_line",
  "city",
  "state",
  "lifecycle_stage",
  "active_deal_count",
  "last_contacted_at",
] as const satisfies readonly (keyof Contacts)[];

/**
 * Fields read through the `companies` relation so a person can surface their
 * company's SHARED line (CONTACT_METHOD_EXCLUSIVITY). Asserted against the
 * nested `companies { … }` selection of GetContacts by
 * tests/contacts/company-line-inherited-methods.test.ts — the flat coverage
 * guard only sees the relation name, not what is inside it.
 */
export const CONTACT_HEADER_COMPANY_FIELDS = [
  "organization_phone",
  "phone_aliases",
  "organization_email",
  "email_aliases",
  "line_contact_id",
] as const satisfies readonly (keyof Companies)[];

/** Guarded against GetContacts by detail-header-model-coverage.test.ts. */
export const CONTACT_HEADER_FIELDS = [
  ...CONTACT_HEADER_SCALAR_FIELDS,
  "companies",
] as const satisfies readonly (keyof Contacts)[];

/**
 * The company relation is typed structurally rather than as the full codegen
 * `Companies` (a hundred-odd columns the header never reads), so a caller — and
 * a test — only has to supply what is actually consumed. A real `Companies`
 * satisfies it.
 */
export type ContactHeaderData = Pick<
  Contacts,
  (typeof CONTACT_HEADER_SCALAR_FIELDS)[number]
> & {
  companies?: CompanySharedMethods | null;
};

/** 1:1 scalar columns edited in the header (dropped from the inline panel). */
export const CONTACT_HEADER_EDITABLE_FIELDS = [
  "email",
  "phone",
  "lifecycle_stage",
] as const satisfies readonly (keyof Contacts)[];
type ContactHeaderEditableField = (typeof CONTACT_HEADER_EDITABLE_FIELDS)[number];

export function buildContactHeaderModel(contact: ContactHeaderData): DetailHeaderModel {
  const subtitleSegments: HeaderSubtitleSegment[] = [];
  if (contact.title) subtitleSegments.push({ text: contact.title });
  if (contact.company_name) {
    subtitleSegments.push({
      text: contact.company_name,
      href: contact.company_id ? `/companies/${contact.company_id}` : undefined,
    });
  }

  // Email + phone are ALWAYS emitted (even when empty) so a missing value shows
  // an inline "Add email" / "Add phone" prompt instead of vanishing — the
  // missing-editable-data inline-add pattern. The view drops the empty prompt
  // for read-only viewers and shows a clickable contact-method link when set.
  const facts: HeaderFact[] = [];
  facts.push({
    id: "email",
    icon: Mail,
    value: contact.email ?? "",
    kind: "email",
    placeholder: "Add email",
    fieldId: "email" satisfies ContactHeaderEditableField,
  });
  facts.push({
    id: "phone",
    icon: Phone,
    value: contact.phone ?? "",
    kind: "phone",
    placeholder: "Add phone",
    fieldId: "phone" satisfies ContactHeaderEditableField,
  });

  // Inherited company line. The shared front desk / role mailbox lives on the
  // COMPANY, so a person who has none of their own would otherwise read as
  // having no phone at all — which is exactly what the 2026-08-21 remediation
  // left behind on ~745 contact rows. Projected here, badged "Company line",
  // and deliberately given no `fieldId`: it is not this row's column, so the
  // header must never offer to edit (i.e. write) it.
  const inherited = resolveInheritedContactMethods(contact, contact.companies);
  const inheritedEmail = inherited.emails[0];
  if (inheritedEmail) facts.push(inheritedFact(inheritedEmail, Mail));
  const inheritedPhone = inherited.phones[0];
  if (inheritedPhone) facts.push(inheritedFact(inheritedPhone, Phone));

  const location = [contact.city, contact.state].filter(Boolean).join(", ");
  if (location) {
    facts.push({ id: "location", icon: MapPin, value: location });
  }

  const metrics: HeaderMetric[] = [];
  if (contact.active_deal_count != null) {
    metrics.push({
      id: "active-deals",
      label: "Active deals",
      value: String(contact.active_deal_count),
    });
  }
  const lastContacted = formatHeaderDate(contact.last_contacted_at);
  if (lastContacted) {
    metrics.push({ id: "last-contacted", label: "Last contacted", value: lastContacted });
  }
  if (contact.lifecycle_stage) {
    metrics.push({
      id: "lifecycle",
      label: "Lifecycle",
      value: contact.lifecycle_stage,
      fieldId: "lifecycle_stage" satisfies ContactHeaderEditableField,
    });
  }

  return { subtitleSegments, facts, metrics };
}

/** One inherited company method as a read-only, badged header fact. */
function inheritedFact(
  method: InheritedContactMethod,
  icon: HeaderFact["icon"]
): HeaderFact {
  return {
    id: `company-line-${method.kind}`,
    icon,
    value: method.value,
    kind: method.kind,
    badge: { text: COMPANY_LINE_BADGE_LABEL, title: inheritedMethodTooltip(method) },
    methodContext: inheritedMethodEntityContext(method) ?? undefined,
  };
}
